/*
 * Purpose: Client session — mock storage or API session + active tenant
 * Last modified: 2026-07-02 | Agent
 * Completeness: 92
 */
import { AUTH_CONFIG, authUrl } from '@/lib/auth-config';
import {
  getTenantById,
  resolveTenantsForEmail,
  type AuthTenant,
} from '@/lib/auth-tenants';

const KEYS = {
  signedIn: 'auth:signed-in',
  email: 'auth:email',
  tenants: 'auth:tenants',
  activeTenant: 'auth:active-tenant',
} as const;

let signedIn = false;
let sessionEmail: string | null = null;
let sessionTenants: AuthTenant[] = [];
let activeTenantId: string | null = null;

function readStorage(): void {
  if (typeof window === 'undefined') return;
  signedIn = window.sessionStorage.getItem(KEYS.signedIn) === '1';
  sessionEmail = window.sessionStorage.getItem(KEYS.email);
  activeTenantId = window.sessionStorage.getItem(KEYS.activeTenant);
  const rawTenants = window.sessionStorage.getItem(KEYS.tenants);
  if (rawTenants) {
    try {
      sessionTenants = JSON.parse(rawTenants) as AuthTenant[];
    } catch {
      sessionTenants = [];
    }
  }
}

function persistSession(): void {
  if (typeof window === 'undefined') return;
  if (signedIn) {
    window.sessionStorage.setItem(KEYS.signedIn, '1');
  } else {
    window.sessionStorage.removeItem(KEYS.signedIn);
  }
  if (sessionEmail) {
    window.sessionStorage.setItem(KEYS.email, sessionEmail);
  } else {
    window.sessionStorage.removeItem(KEYS.email);
  }
  if (sessionTenants.length) {
    window.sessionStorage.setItem(KEYS.tenants, JSON.stringify(sessionTenants));
  } else {
    window.sessionStorage.removeItem(KEYS.tenants);
  }
  if (activeTenantId) {
    window.sessionStorage.setItem(KEYS.activeTenant, activeTenantId);
  } else {
    window.sessionStorage.removeItem(KEYS.activeTenant);
  }
}

readStorage();

export function isAuthenticated(): boolean {
  readStorage();
  return signedIn;
}

export async function isAuthenticatedAsync(): Promise<boolean> {
  if (AUTH_CONFIG.mock) return isAuthenticated();
  try {
    const res = await fetch(authUrl(AUTH_CONFIG.endpoints.session), {
      credentials: 'include',
      cache: 'no-store',
    });
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return false;
    if (!res.ok) return false;
    const json = (await res.json()) as {
      ok?: boolean;
      user?: { email?: string };
      tenants?: AuthTenant[];
      activeTenantId?: string;
    };
    if (json.ok && json.user?.email) {
      signedIn = true;
      sessionEmail = json.user.email;
      sessionTenants = json.tenants ?? resolveTenantsForEmail(json.user.email);
      activeTenantId =
        json.activeTenantId ??
        (sessionTenants.length === 1 ? sessionTenants[0]?.id ?? null : null);
      persistSession();
      return true;
    }
    signedIn = false;
    sessionEmail = null;
    sessionTenants = [];
    activeTenantId = null;
    persistSession();
    return false;
  } catch {
    return false;
  }
}

export function getSessionEmail(): string | null {
  readStorage();
  return sessionEmail;
}

export function getSessionTenants(): AuthTenant[] {
  readStorage();
  return sessionTenants;
}

export function getActiveTenant(): AuthTenant | null {
  readStorage();
  return getTenantById(sessionTenants, activeTenantId);
}

export function getActiveTenantId(): string | null {
  readStorage();
  return activeTenantId;
}

export interface SignInOptions {
  tenants?: AuthTenant[];
  activeTenantId?: string;
}

export function signIn(email: string, options?: SignInOptions): void {
  signedIn = true;
  sessionEmail = email.trim().toLowerCase();
  sessionTenants = options?.tenants ?? resolveTenantsForEmail(sessionEmail);
  activeTenantId =
    options?.activeTenantId ??
    (sessionTenants.length === 1 ? sessionTenants[0].id : null);
  persistSession();
}

export function setActiveTenant(tenantId: string): void {
  readStorage();
  if (!sessionTenants.some((t) => t.id === tenantId)) return;
  activeTenantId = tenantId;
  persistSession();
}

export async function setActiveTenantAsync(tenantId: string): Promise<void> {
  setActiveTenant(tenantId);
  if (AUTH_CONFIG.mock) return;
  try {
    const res = await fetch(authUrl(AUTH_CONFIG.endpoints.tenant), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId }),
    });
    if (!res.ok) return;
    const json = (await res.json()) as {
      ok?: boolean;
      activeTenantId?: string;
      tenants?: AuthTenant[];
    };
    if (json.ok && json.activeTenantId) {
      activeTenantId = json.activeTenantId;
      if (json.tenants?.length) sessionTenants = json.tenants;
      persistSession();
    }
  } catch {
    /* local session already updated — server sync best-effort */
  }
}

export function needsTenantSelection(): boolean {
  readStorage();
  return signedIn && sessionTenants.length > 1 && !activeTenantId;
}

export async function signOutUnified(): Promise<void> {
  signedIn = false;
  sessionEmail = null;
  sessionTenants = [];
  activeTenantId = null;
  if (typeof window !== 'undefined') {
    Object.values(KEYS).forEach((k) => window.sessionStorage.removeItem(k));
  }
  if (!AUTH_CONFIG.mock) {
    await fetch(authUrl(AUTH_CONFIG.endpoints.logout), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => undefined);
  }
}
